import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './auth.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG, SierraTheme } from '@card-software/library';
import { siteInterceptor, errorInterceptor, SITE_API_BASE_URL } from '@card-software/nexus-utilities';
import { environment } from '../environments/environment';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { PrimengModule } from './primeng/primeng.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { AngularMaterialModule } from './angular-material/angular-material.module';
import { UserDetailsComponent } from './components/users-manager/user-details/user-details.component';
import { UsersComponent } from './components/users-manager/users/users.component';
import { UserModalComponent } from './components/modals/user-modal/user-modal.component';
import { UserSetRolesComponent } from './components/modals/user-set-roles/user-set-roles.component';
import { UserSetGroupsComponent } from './components/modals/user-set-groups/user-set-groups.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DepartmentsComponent } from './components/deparment-manager/departments/departments.component';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { DepartmentModalComponent } from './components/modals/department-modal/department-modal.component';
import { UserAuthenticationService } from './services/user-authentication.service';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { SigningDashboardComponent } from './components/signing-manager/signing-dashboard/signing-dashboard.component';
import { SessionHistoryComponent } from './components/signing-manager/session-history/session-history.component';
import { SigningSessionBarComponent } from './components/signing-manager/signing-session-bar/signing-session-bar.component';
import { SigningOtpDialogComponent } from './components/signing-manager/signing-otp-dialog/signing-otp-dialog.component';
import { SigningAuthenticatorSetupComponent } from './components/signing-manager/signing-authenticator-setup/signing-authenticator-setup.component';
import { ApprovalPoliciesComponent } from './components/approval-policies/approval-policies/approval-policies.component';
import { ApprovalPolicyDetailsComponent } from './components/approval-policies/approval-policy-details/approval-policy-details.component';
import { AccessCatalogComponent } from './components/access-catalog/access-catalog.component';

@NgModule({
  declarations: [
    AppComponent,
    UserDetailsComponent,
    UsersComponent,
    UserModalComponent,
    UserSetRolesComponent,
    UserSetGroupsComponent,
    DepartmentsComponent,
    DepartmentModalComponent,
    UnauthorizedComponent,
    SigningDashboardComponent,
    SessionHistoryComponent,
    SigningSessionBarComponent,
    SigningOtpDialogComponent,
    SigningAuthenticatorSetupComponent,
    ApprovalPoliciesComponent,
    ApprovalPolicyDetailsComponent,
    AccessCatalogComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    CardSoftwareLibraryModule,
    CommonModule,
    PrimengModule,
    AngularMaterialModule,
    TranslateLocalizedPipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
    TranslateWithFallbackPipe,
  ],
  exports: [TranslateWithFallbackPipe, TranslateLocalizedPipe],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra User Management',
        brandUrl: 'https://www.card-software.com',
        contactEmail: 'info@card-software.com',
        redirectUrl: '/',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    MessageService,
  ],

  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(translate: TranslateService) {
    const savedLang = localStorage.getItem('app_lang');
    if (savedLang) {
      translate.setDefaultLang(savedLang);
      translate.use(savedLang);
    } else {
      translate.setDefaultLang('en');
      translate.use('en');
    }
  }
}
